/**
 * SandboxSettingsOverlay — slide-up panel over the sandbox canvas holding the
 * sandbox's metadata (Purpose + Instance summary), opened from the header gear.
 * Same motion pattern as SandboxLogsOverlay / the Flow Designer settings
 * overlay: a background slides up, a close button, content fades in. Render
 * under <AnimatePresence> in the canvas so the exit animation plays.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Button, Text } from '@chatsdet/ui';
import { Gear, X } from '@phosphor-icons/react';
import type { EnvironmentInstance } from '../../../hooks/useEnvironmentInstances';
import { PurposeCard } from './overview/PurposeCard';
import { InstanceSummaryCard } from './overview/InstanceSummaryCard';

const EASE = [0.32, 0.72, 0, 1] as const;

export interface SandboxSettingsOverlayProps {
  isOpen: boolean;
  instance: EnvironmentInstance;
  isSuperadmin: boolean;
  onClose: () => void;
}

export function SandboxSettingsOverlay({
  isOpen,
  instance,
  isSuperadmin,
  onClose,
}: SandboxSettingsOverlayProps) {
  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: EASE } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: EASE } }}
        className="absolute inset-0 z-40 bg-background"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-2 right-2 z-50"
      >
        <Button variant="icon" iconOnly tooltip="Close" onPress={onClose}>
          <X size={20} />
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex flex-col"
      >
        <div className="flex items-center gap-2 px-4 h-14 shrink-0 border-b border-border">
          <Gear size={16} weight="fill" className="text-muted-foreground" />
          <Text weight="semibold" className="text-base">
            Sandbox details
          </Text>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl px-6 py-6">
            <PurposeCard instance={instance} />
            <InstanceSummaryCard instance={instance} isSuperadmin={isSuperadmin} />
          </div>
        </div>
      </motion.div>
    </>
  );
}

SandboxSettingsOverlay.displayName = 'SandboxSettingsOverlay';
